import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Utils, calculateGrowthPlanEstimate, PLAN_TYPE_CODES, resolveBillingGeoHeaders, executePlanSelection, getPlanCheckoutIntent, DEFAULT_BILLING_CURRENCY, can , formatMetricPrice} from '../../utils';
import PlanMetricsList from './PlanMetricsList';
import './plan.css';

const Plan = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle] = useState('monthly'); // annual toggle removed; plans bill monthly here
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [currencyMeta, setCurrencyMeta] = useState(null);
    const [regionLabel, setRegionLabel] = useState(null);
    const [freeLimits, setFreeLimits] = useState([]);
    const [growthPricing, setGrowthPricing] = useState([]);
    const [usageByType, setUsageByType] = useState({});
    const [geoReady, setGeoReady] = useState(false);
    const [geoResolving, setGeoResolving] = useState(true);
    const [billingCountryCode, setBillingCountryCode] = useState(null);
    const [currentPlanType, setCurrentPlanType] = useState(null);
    const freeCheckoutResumedRef = useRef(false);

    const userData = JSON.parse(localStorage.getItem('userData') || 'null');

    const faqs = [
        {
            question: 'What happens when I exceed my free tier limits?',
            answer: 'When you exceed your free tier limits, you can upgrade to the Growth plan for additional capacity. We\'ll notify you before you reach your limits so you can plan accordingly.'
        },
        {
            question: 'Can I switch between plans at any time?',
            answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any charges or credits.'
        },
        {
            question: 'Is there a contract or commitment?',
            answer: 'No, all our plans are month-to-month with no long-term commitment. You can cancel anytime without penalty.'
        },
        {
            question: 'What payment methods do you accept?',
            answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and can arrange invoicing for annual plans.'
        },
        {
            question: 'Do you offer discounts for annual billing?',
            answer: 'Yes! When you choose annual billing, you get 2 months free compared to monthly billing.'
        }
    ];

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setGeoResolving(true);
            try {
                const headers = await resolveBillingGeoHeaders();
                const cc = headers['x-billing-country'] || null;
                if (!cancelled) {
                    setBillingCountryCode(cc);
                    setGeoReady(true);
                }
                await fetchPlans(headers, cancelled);
            } catch (error) {
                console.error('Billing region resolution:', error);
                if (!cancelled) {
                    setGeoReady(false);
                    setPlans([]);
                }
            } finally {
                if (!cancelled) {
                    setGeoResolving(false);
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (freeCheckoutResumedRef.current || !geoReady || loading || !userData?.id || plans.length === 0) return;
        const intent = getPlanCheckoutIntent();
        if (!intent || Number(intent.plan?.plan_type) !== PLAN_TYPE_CODES[0].value) return;
        const plan = plans.find((p) => Number(p.id) === Number(intent.plan.id)) || intent.plan;
        const geoHeaders = billingCountryCode
            ? { 'x-billing-country': billingCountryCode }
            : {};
        executePlanSelection({
            plan,
            billingCycle: intent.billingCycle || 'monthly',
            userData,
            navigate,
            geoHeaders,
        }).then((done) => {
            if (done) freeCheckoutResumedRef.current = true;
        });
    }, [geoReady, loading, userData, plans, billingCountryCode, navigate]);

    // Detect the user's active plan so we can show a "Your current plan" card
    // and mark the matching pricing card. No sub → Free (default).
    useEffect(() => {
        if (!userData?.id) return;
        fetch(Utils.baseurl + 'user/getbyid', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: userData.id }),
        })
            .then((r) => r.json())
            .then((result) => {
                if (result.status) {
                    const pt = result.data?.sub?.plan_type;
                    setCurrentPlanType(pt ? Number(pt) : PLAN_TYPE_CODES[0].value);
                }
            })
            .catch(() => setCurrentPlanType(PLAN_TYPE_CODES[0].value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPlans = async (geoHeaders = {}, cancelled = false) => {
        try {
            const response = await fetch(Utils.baseurl + 'public/plans', {
                headers: geoHeaders,
            });
            const result = await response.json();
            if (cancelled) return;
            if (result.status && result.data?.length > 0) {
                setPlans(result.data);
                setRegionLabel(result.currency?.country_code ?? null);
                setCurrencyMeta(result.currency ?? null);
                setFreeLimits(result.free_limits || []);
                const growth = result.data.find((p) => Number(p.plan_type) === PLAN_TYPE_CODES[1].value);
                const pricing = growth?.pricing || result.pricing || [];
                setGrowthPricing(pricing);
                const initialUsage = {};
                pricing.forEach((row) => {
                    const type = Number(row.type);
                    if (type > 0) initialUsage[type] = 0;
                });
                setUsageByType(initialUsage);
            } else {
                setPlans([]);
            }
        } catch (error) {
            if (!cancelled) {
                console.error('Error fetching plans:', error);
                setPlans([]);
            }
        }
    };

    const growthPlan = useMemo(
        () => plans.find((p) => Number(p.plan_type) === PLAN_TYPE_CODES[1].value),
        [plans]
    );

    const enterprisePlan = useMemo(
        () => plans.find((p) => Number(p.plan_type) === PLAN_TYPE_CODES[2].value),
        [plans]
    );

    // The plan the user is currently on (falls back to the Free plan card).
    const currentPlan = useMemo(() => {
        if (!plans.length || currentPlanType == null) return null;
        return plans.find((p) => Number(p.plan_type) === Number(currentPlanType))
            || plans.find((p) => Number(p.plan_type) === PLAN_TYPE_CODES[0].value)
            || null;
    }, [plans, currentPlanType]);

    // What to offer as the next step up: Free → Growth, Growth → Enterprise, Enterprise → none.
    const upgradeTarget = useMemo(() => {
        if (currentPlanType === PLAN_TYPE_CODES[0].value) return growthPlan || null;
        if (currentPlanType === PLAN_TYPE_CODES[1].value) return enterprisePlan || null;
        return null;
    }, [currentPlanType, growthPlan, enterprisePlan]);

    const upgradeLabel = currentPlanType === PLAN_TYPE_CODES[1].value
        ? 'Contact Sales'
        : `Upgrade to ${growthPlan?.name || 'Growth'}`;

    const calculatorMetrics = useMemo(
        () => growthPricing.filter((row) => Number(row.type) > 0),
        [growthPricing]
    );

    // Calculator: base + (usage × rate) — same as paid-plan billing (full usage).
    const estimate = useMemo(
        () => calculateGrowthPlanEstimate({
            pricing: growthPricing,
            freeLimits: [],
            usageByType,
            billingCycle,
        }),
        [growthPricing, usageByType, billingCycle]
    );

    const currencySymbol = currencyMeta?.symbol ?? DEFAULT_BILLING_CURRENCY?.symbol ?? '';

    const displayEstimate = billingCycle === 'annual'
        ? estimate.total
        : estimate.monthlyEquivalent;

    const formatCalcMoney = (amount) =>
        `${currencySymbol}${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    function handleSavePlan(plan) {
        if (!can("billing.manage")) {
            toast.error('You do not have permission to change the plan.');
            return;
        }
        if (!geoReady) {
            toast.error('Regional pricing is still loading. Please try again in a moment.');
            return;
        }

        const geoHeaders = billingCountryCode
            ? { 'x-billing-country': billingCountryCode }
            : {};

        executePlanSelection({
            plan,
            billingCycle,
            userData,
            navigate,
            geoHeaders,
        });
    }

    const setMetricUsage = (type, value) => {
        const n = Number(type);
        setUsageByType((prev) => ({ ...prev, [n]: Math.max(0, value) }));
    };

    if (loading || geoResolving) {
        return (
            <div className="plan-loading">
                <div className="plan-loading-spinner"></div>
                <p>Detecting your region and loading plans...</p>
            </div>
        );
    }

    return (
        <div className="plan-page">
            <section className="plan-header">
                <h1>Transparent pricing for<br />every stage of <span className="gradient-text">growth</span></h1>
                <p className="plan-subtitle">Choose the right plan for your business</p>
                {regionLabel && currencyMeta?.label && (
                    <p className="plan-region-hint">
                        Prices shown for <strong>{regionLabel}</strong> ({currencyMeta.label})
                    </p>
                )}
            </section>

            {currentPlan && (
                <section className="my-plan-section">
                    <div className="my-plan-card">
                        <div className="my-plan-info">
                            <span className="my-plan-eyebrow">Your current plan</span>
                            <h2 className="my-plan-name">{currentPlan.name}</h2>
                            <div className="my-plan-price">
                                {Number(currentPlan.plan_type) === PLAN_TYPE_CODES[1].value && !currentPlan.is_contact_sales && (
                                    <span className="starts-at">Starts at</span>
                                )}
                                <span className="my-plan-price-amount">{currentPlan.price}</span>
                                {!currentPlan.is_contact_sales && Number(currentPlan.plan_type) !== PLAN_TYPE_CODES[2].value && (
                                    <span className="my-plan-price-period">/mo</span>
                                )}
                            </div>
                            {currentPlan.description && <p className="my-plan-desc">{currentPlan.description}</p>}
                        </div>
                        {can("billing.manage") && upgradeTarget && (
                            <div className="my-plan-action">
                                <button
                                    type="button"
                                    className="plan-button filled my-plan-upgrade"
                                    onClick={() => handleSavePlan(upgradeTarget)}
                                    disabled={!geoReady}
                                >
                                    {upgradeLabel}
                                </button>
                                <span className="my-plan-action-hint">Only pay for what you use beyond the free limits.</span>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section className="plan-cards-section">
                {currentPlan && <h2 className="plan-compare-heading">Compare plans</h2>}
                <div className="plan-cards-container">
                    {plans.map((plan) => {
                        const isCurrent = currentPlan && Number(plan.plan_type) === Number(currentPlanType);
                        return (
                        <div key={plan.id} className={`plan-card ${plan.isPopular ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}>
                            {isCurrent ? (
                                <div className="popular-badge current-badge">Current plan</div>
                            ) : plan.isPopular ? (
                                <div className="popular-badge">Most Popular</div>
                            ) : null}
                            <div className="plan-card-header">
                                <h3>{plan.name}</h3>
                                <div className="plan-price">
                                    {Number(plan.plan_type) === PLAN_TYPE_CODES[1].value && !plan.is_contact_sales && (
                                        <span className="starts-at">Starts at</span>
                                    )}
                                    <span className="price">{plan.price}</span>
                                    {!plan.is_contact_sales && Number(plan.plan_type) !== PLAN_TYPE_CODES[2].value && (
                                        <span className="period">/mo</span>
                                    )}
                                </div>
                                <p className="plan-description">{plan.description}</p>
                                {plan.subtitle && <p className="plan-subtitle-text">{plan.subtitle}</p>}
                            </div>

                            {isCurrent ? (
                                <button type="button" className="plan-button current-plan-btn" disabled>
                                    ✓ Current Plan
                                </button>
                            ) : can("billing.manage") ? (
                                <button
                                    type="button"
                                    className={`plan-button ${plan.buttonStyle || 'outline'}`}
                                    onClick={() => handleSavePlan(plan)}
                                    disabled={!geoReady}
                                >
                                    {plan.buttonText || 'Get Started'}
                                </button>
                            ) : (
                                <button type="button" className="plan-button outline" disabled>
                                    View only
                                </button>
                            )}

                            <div className="plan-features-list">
                                <PlanMetricsList plan={plan} currencySymbol={currencySymbol} />
                            </div>
                        </div>
                        );
                    })}
                </div>
            </section>

            {growthPlan && calculatorMetrics.length > 0 && (
                <section className="plan-calculator-section">
                    <div className="calculator-container">
                        <h2>Growth plan cost calculator</h2>
                        <p className="section-subtitle">Estimate cost from base + usage (per unit)</p>

                        <div className="calculator-content">
                            <div className="calculator-inputs">
                                {calculatorMetrics.map((row) => {
                                    const type = Number(row.type);
                                    const value = usageByType[type] ?? 0;
                                    return (
                                        <div className="input-group" key={type}>
                                            <label>{row.type_label}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                className="calculator-number-input"
                                                value={value}
                                                onChange={(e) => setMetricUsage(type, parseInt(e.target.value, 10) || 0)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="calculator-result">
                                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                    {[
                                        ['Base', estimate.base],
                                        ['Usage', estimate.variable],
                                    ].map(([label, amount]) => (
                                        <div
                                            key={label}
                                            style={{
                                                flex: 1,
                                                background: '#f9fafb',
                                                borderRadius: 8,
                                                padding: '8px 10px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{label}</div>
                                            <strong style={{ fontSize: 13, color: '#111827' }}>
                                                {formatCalcMoney(amount)}
                                            </strong>
                                        </div>
                                    ))}
                                </div>
                                <div className="result-label">
                                    Estimated {billingCycle === 'annual' ? 'Annual' : 'Monthly'} Cost
                                </div>
                                <div className="result-price">
                                    {formatCalcMoney(displayEstimate)}
                                    <span>{billingCycle === 'annual' ? '/yr' : '/mo'}</span>
                                </div>
                                <ul className="calculator-usage-breakdown">
                                    {calculatorMetrics.map((row) => {
                                        const type = Number(row.type);
                                        const value = usageByType[type] ?? 0;
                                        const unitPrice = parseFloat(row.price) || 0;
                                        const lineCost = value * unitPrice;
                                        return (
                                            <li key={type} className={value > 0 ? 'has-usage' : ''}>
                                                <span className="breakdown-label">{row.type_label}</span>
                                                <span className="breakdown-calc">
                                                    {Number(value).toLocaleString()} × {formatMetricPrice(currencySymbol, unitPrice)}
                                                    {' = '}
                                                    <strong>{formatCalcMoney(lineCost)}</strong>
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {can("billing.manage") ? (
                                <button
                                    type="button"
                                    className="plan-button filled calculator-cta"
                                    onClick={() => handleSavePlan(growthPlan)}
                                    disabled={!geoReady}
                                >
                                    {growthPlan.buttonText || 'Start Growth plan'}
                                </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="plan-faq-section">
                <h2>Frequently asked questions</h2>
                <div className="faq-list">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                            onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        >
                            <div className="faq-question">
                                <span>{faq.question}</span>
                                <svg className="faq-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d={expandedFaq === index ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                </svg>
                            </div>
                            {expandedFaq === index && (
                                <div className="faq-answer">{faq.answer}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Plan;
